/** @jsxImportSource @emotion/react */
import { AxiosError } from "axios";
import { Button, Container, Link, Stack, Text } from "basis";
import { Otp } from "components/Otp/Otp";
import { SvgInline } from "components/SvgInline/SvgInline";
import { getHref } from "library/api";
import { FullAddress } from "library/api/address/types.generated";
import { OtpRequiredErrorType } from "library/api/otp/otp";
import { ProductScheme } from "library/hooks/types";
import { FullPersonalDetails } from "pages/ManagePersonalDetails/types";
import { Dispatch, useState } from "react";
import { useMutation } from "react-query";
import { Action, ActionType } from "./types";

interface Props {
  cardEndsWith: string;
  scheme: ProductScheme;
  personalDetailsUrl: string;
  lostStolenDamagedUrl: string;
  token: string;
  dispatch: Dispatch<Action>;
}
export const ReportLostStolenOrDamaged = ({
  cardEndsWith,
  scheme,
  personalDetailsUrl,
  token,
  lostStolenDamagedUrl,
  dispatch,
}: Props) => {
  const [otpState, setOtpState] = useState<{
    createSessionUrl: string;
    token: string;
    isLostOrStolen: boolean;
  } | null>(null);

  const personalDetailsMutation = useMutation<
    FullAddress,
    AxiosError | AxiosError<OtpRequiredErrorType>,
    boolean
  >(
    personalDetailsUrl,
    async () => {
      const response = await getHref<FullPersonalDetails>(personalDetailsUrl);

      return (response?.data.mailing ??
        response?.data.residential) as FullAddress;
    },
    {
      onSuccess(response, isLostOrStolen) {
        dispatch({
          type: isLostOrStolen
            ? ActionType.REPORT_LOST_STOLEN
            : ActionType.REPORT_DAMAGED,
          address: response,
          cardEndsWith,
          lostStolenDamagedUrl,
          scheme,
        });
      },
      onError(error, isLostOrStolen) {
        if (error.response?.data.type === "2FA_REQUIRED") {
          const createSessionUrl = error.response?.data.links.find(
            (link) => link.rel === "sessions",
          )?.href;

          if (!createSessionUrl) {
            console.error("Missing Create Session Url from payload");
            dispatch({ type: ActionType.ERROR });
            return;
          }

          setOtpState({
            token,
            createSessionUrl,
            isLostOrStolen,
          });
          return;
        }

        dispatch({ type: ActionType.ERROR });
      },
    },
  );

  if (otpState) {
    return (
      <Otp
        createSessionUrl={otpState.createSessionUrl}
        token={otpState.token}
        onSuccess={() => {
          personalDetailsMutation.mutate(otpState.isLostOrStolen);
        }}
        onBackClick={() => {
          setOtpState(null);
        }}
      />
    );
  }

  return (
    <Stack gap="8">
      <SvgInline name="lost-stolen-damaged-card" size="120" />
      <Text textStyle="heading4">Report lost, stolen or damaged card</Text>
      {scheme !== "NON_SCHEME" ? (
        <Text>
          If your card ending with {cardEndsWith} has been lost, stolen or
          damaged, we’ll send you a replacement and update your mobile digital
          wallet automatically to get you going straight away.
        </Text>
      ) : (
        <Text>
          If your card ending with {cardEndsWith} has been lost, stolen or
          damaged, we'll send you a replacement.
        </Text>
      )}
      <Text>
        If a secondary card has been lost, stolen or damaged, please visit
        Latitude <Link href="/contact">customer support</Link>.
      </Text>
      <Container>
        <div css={{ display: "flex", gap: 16 }}>
          <Button
            width="256"
            onClick={() => {
              personalDetailsMutation.mutate(true);
            }}
          >
            Report lost or stolen card
          </Button>
          <Button
            width="256"
            onClick={() => {
              personalDetailsMutation.mutate(false);
            }}
          >
            Report damaged card
          </Button>
        </div>
      </Container>
    </Stack>
  );
};
